import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router } from '@angular/router';
import { PopoverController, ModalController } from '@ionic/angular';
import { firstValueFrom, Subscription } from 'rxjs';
import { ActiveToast, ToastrService } from 'ngx-toastr';

import { DeleteRecordingPage } from '../../modals/delete-recording/delete-recording.page';
import { SessionsService } from '../../services/sessions/sessions.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { IdTokenService } from '../../services/id-token/id-token.service';
import { DownloadLinkMenuComponent } from '../download-link-menu/download-link-menu.component';
import { DownloadMenuComponent } from '../download-menu/download-menu.component';
import { UserModel } from '@sc/types';
import { UserService } from '../../services/user/user.service';
import { Plan } from '@sc/types';
import { WalletService } from '../../services/wallet/wallet.service';
import { Recording } from '@sc/types';
import { FarnsworthService } from '../../services/farnsworth/farnsworth.service';
import { BendixService } from '../../services/bendix/bendix.service';
import { RoleLimits } from '@sc/types';
import { RolesService } from '../../services/roles/roles.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { Session } from '@sc/types';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
// import { EditInDescriptMenuComponent } from '../edit-in-descript-menu/edit-in-descript-menu.component';
import { DescriptService } from '../../services/descript/descript.service';

@Component({
  selector: 'sc-recording-menu',
  templateUrl: './recording-menu.component.html',
  styleUrls: ['./recording-menu.component.scss'],
})
export class RecordingMenuComponent implements OnInit, OnDestroy {
  @Input() recording: Recording;
  @Input() event: any;
  @Input() integrations: any;
  studioSession$ = this.sessionsService.studioSession$;
  role$ = this.rolesService.role$;
  roleLimits = RoleLimits;
  hostID: string;
  castMemberID: string;
  deleteRecordingModal: any;
  statsForNerdsModal: any;
  user: UserModel;
  subs: Array<Subscription> = [];
  enableRenderBtn = true;
  enhanced = false;
  mix = false;
  dropboxConnected = false;
  guestInviteLink: string;
  mp4DownloadLink: string;
  webmDownloadLink: string;
  wavDownloadLink: string;
  mp3DownloadLink: string;
  plan: Plan;
  session: Session;

  constructor(
    private analyticsService: AnalyticsService,
    private bendixService: BendixService,
    private descriptService: DescriptService,
    private farnsworthService: FarnsworthService,
    private idTokenService: IdTokenService,
    private popoverController: PopoverController,
    private modalController: ModalController,
    private rolesService: RolesService,
    public router: Router,
    private sessionsService: SessionsService,
    private toastrService: ToastrService,
    private userService: UserService,
    private walletService: WalletService
  ) {}

  async ngOnInit() {
    this.user = await this.userService.getUser();

    this.setupDetails();
    this.setupIntegrations();
    this.setupPlan();
    this.setupSession();
  }

  setupDetails() {
    if (this.recording.fileName.includes('enhanced')) {
      this.enhanced = true;
    }
    if (this.recording.castMemberName === 'mix' || this.recording.fileName.includes('mix')) {
      this.mix = true;
    }
  }

  setupSession() {
    const sub = this.sessionsService.getSessionByID(this.recording.sessionID).subscribe((session) => {
      this.session = session;
    });

    this.subs.push(sub);
  }

  setupIntegrations() {
    this.dropboxConnected = this.integrations?.dropbox;
  }

  async copyLink(click: CustomEvent | any) {
    await this.popoverController.dismiss();
    const popover = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: DownloadLinkMenuComponent,
      componentProps: {
        event: this.event,
        recording: this.recording,
      },
      cssClass: 'download-link-menu',
      event: this.event,
    });
    await popover.present();
    await this.analyticsService.track('opened copy recording share link', {
      fileName: this.recording.fileName,
      recordingID: this.recording.recordingID,
      sessionID: this.recording.sessionID,
      showID: this.recording.showID,
    });
    await popover.onDidDismiss();
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: RecordingMenuComponent,
      componentProps: {
        event: this.event,
        recording: this.recording,
      },
      cssClass: 'autoWidth',
      event: this.event,
    });
    await menu.present();
  }

  async openEditInDescriptMenu() {
    this.popoverController.dismiss();
    this.descriptService.editInDescript([this.recording]);
  }

  async openDownloadMenu() {
    await this.popoverController.dismiss();
    const popover = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: DownloadMenuComponent,
      componentProps: {
        event: this.event,
        recording: this.recording,
      },
      cssClass: 'download-menu',
      event: this.event,
    });
    await popover.present();
    await this.analyticsService.track('opened download recording', {
      fileName: this.recording.fileName,
      recordingID: this.recording.recordingID,
      sessionID: this.recording.sessionID,
      showID: this.recording.showID,
    });
    await popover.onDidDismiss();
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: RecordingMenuComponent,
      componentProps: {
        event: this.event,
        recording: this.recording,
      },
      cssClass: 'autoWidth',
      event: this.event,
    });
    await menu.present();
  }

  async openDeleteRecordingModal() {
    this.deleteRecordingModal = await this.modalController.create({
      component: DeleteRecordingPage,
      componentProps: {
        recording: this.recording,
        session: this.studioSession$.value,
      },
      showBackdrop: true,
      backdropDismiss: true,
      animated: true,
      cssClass: 'delete-recording-modal',
    });
    await this.analyticsService.track('opened delete recording', {
      fileName: this.recording.fileName,
      recordingID: this.recording.recordingID,
      sessionID: this.recording.sessionID,
      showID: this.recording.showID,
    });
    await this.deleteRecordingModal.present();
    await this.popoverController.dismiss();
  }

  timeoutRerender() {
    this.enableRenderBtn = false;
    setTimeout(() => {
      this.enableRenderBtn = true;
    }, 10000);
  }

  async reRenderRecording() {
    // this.popoverController.dismiss();
    // TOD [SC-56]O: Change timeoutRerender() to a stateful boolean.
    try {
      const idToken = await this.idTokenService.getFreshIdToken();

      let service;
      if (this.recording.fileName.includes('screen')) {
        await firstValueFrom(this.farnsworthService.processRecording(this.recording, idToken));
      } else {
        // Need to tell the render to also render Video if the recording was a video recording.
        if (this.recording.hadVideoPlanAtRecording && this.session.videoEnabled) {
          this.recording.renderVideo = true;
        }
        await firstValueFrom(
          this.bendixService.processRecording({ ...this.recording, iOS: true, preview: false }, idToken)
        );
      }

      this.toastrService.success(this.recording.fileName, `Started rendering Recording`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: false,
        timeOut: 5 * 1000,
        toastComponent: GeneralToastComponent,
      });

      await this.analyticsService.track('rerendered recording', {
        fileName: this.recording.fileName,
        recordingID: this.recording.recordingID,
        sessionID: this.recording.sessionID,
        showID: this.recording.showID,
        success: true,
      });
    } catch (error) {
      // console.log(`🐛 🐞 error.message ➡ ${JSON.stringify(error.message, null, 2)} 🐞 🐛 `);
      if (error.status === 429) {
        const messages = `Already rendering ${this.recording.fileName}.wav`;

        this.toastrService.info('File is currently processing, this may take a few minutes', messages, {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 5 * 1000,
          toastComponent: GeneralToastComponent,
        });
        return;
      }
      // this.recording.error = error.error.error;
      const message = `Failed to render ${this.recording.fileName}.wav`;

      const toast: ActiveToast<GeneralToastComponent> = this.toastrService.error(
        error.error.error.includes('no chunks')
          ? 'No files found. Cloud Recordings are always available as a backup'
          : `Cloud Recordings are always available as a backup`,
        message,
        {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 5 * 1000,
          toastComponent: GeneralToastComponent,
        }
      );

      toast.toastRef.componentInstance.learnMoreTopic = 'cloudRecordings';

      await this.analyticsService.track('rerendered recording', {
        fileName: this.recording.fileName,
        recordingID: this.recording.recordingID,
        sessionID: this.recording.sessionID,
        showID: this.recording.showID,
        success: false,
      });
    }
  }

  setupPlan() {
    const sub = this.walletService.dashboardPlan$.subscribe((plan: Plan) => {
      if (!plan) {
        return;
      }

      this.plan = plan;
    });

    this.subs.push(sub);
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
